import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import type { RewardTier } from '@sq/shared';
import { Blueprint, KitDiagram } from '@sq/ui';
import { Api } from '../lib/api.ts';
import { useQuest } from '../lib/store.tsx';
import { TopBar, Footer } from '../components/TopBar.tsx';
import { LeaderboardRow } from '../components/LeaderboardRow.tsx';
import { loadSummary, type RunSummary } from '../lib/run.ts';

type Claim = { tier: RewardTier; dataUrl: string; expiresAt: number; payload: string };


const REJECT_COPY: Record<string, string> = {
  TOO_FAST: 'That run finished faster than the game allows, so the server couldn’t verify it.',
  CHECKPOINTS_MISSING: 'Some rounds didn’t reach the server (patchy Wi-Fi?), so it couldn’t verify the run.',
  REPLAY_REJECTED: 'The server couldn’t replay one of your rounds, so it couldn’t verify the run.',
  SCORE_IMPLAUSIBLE: 'That score is outside what the game allows, so it wasn’t recorded.',
  ATTEMPT_EXPIRED: 'This run took too long to report back.',
};

export const ROUND_NAMES = ['On the host', 'In a container', 'In a sandbox'];

/** Why each round scored what it did, from the replayed counts. */
export function roundWhy(i: number, r: RunSummary['rounds'][number]): string {
  const asked = `${r.prompts} approval${r.prompts === 1 ? '' : 's'} asked`;
  const handled = r.blocked + r.allowed;
  if (i === 0) return `${asked}. No boundary: every risky request was on you.`;
  if (i === 1) return `${asked} · ${r.blocked} host-file read${r.blocked === 1 ? '' : 's'} blocked by the wall`;
  return `${asked} · ${handled} handled by the sandbox`;
}

/** Which full-screen step of the post-run flow is showing: the results, the sandbox recap, the kits pitch, then the reward. */
type Step = 'results' | 'sandbox' | 'kits' | null;

/** Results: the same work in three environments, side by side. Stays until the player continues. */
function Results({ summary, onDone }: { summary: RunSummary; onDone: () => void }) {
  const max = Math.max(1, ...summary.rounds.map((r) => r.score));
  const best = summary.rounds.reduce((b, r, i) => (r.score > summary.rounds[b].score ? i : b), 0);
  return (
    <section className="recap" data-testid="recap" aria-labelledby="recap-title">
      <p className="eyebrow">Run complete{summary.assist ? ' · assist mode' : ''}</p>
      <h1 className="h1 recap-title" id="recap-title">
        Stop babysitting your agent. <em>Start building.</em>
      </h1>
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p className="eyebrow" style={{ margin: 0 }}>Same work. Different boundaries.</p>
        <div className="results-bars" data-testid="results-bars">
          {summary.rounds.map((r, i) => (
            <div key={i} className={`results-bar${i === best ? ' best' : ''}`} data-testid={`results-round-${i + 1}`}>
              <span className="name">{ROUND_NAMES[i]}</span>
              <span className="track" aria-hidden="true">
                <span className="fill" style={{ display: 'block', width: `${Math.max(2, (Math.max(0, r.score) / max) * 100)}%` }} />
              </span>
              <span className="val">{Math.max(0, r.score).toLocaleString('en-US')}</span>
              <span className="why">
                {roundWhy(i, r)}
                {r.breaches > 0 ? ` · ${r.breaches} risky approval${r.breaches === 1 ? '' : 's'}` : ''}
              </span>
            </div>
          ))}
        </div>
        <div className="results-total">
          <span className="eyebrow">Total</span>
          <b data-testid="results-total">{Math.max(0, summary.total).toLocaleString('en-US')}</b>
        </div>
      </div>
      <button className="btn primary big block" onClick={onDone} data-testid="recap-continue">
        Continue →
      </button>
    </section>
  );
}

/** The explanatory reveal: the completed sandbox architecture plus the takeaway. Stays until the player continues. */
export function SandboxRecap({ onDone, animate }: { onDone: () => void; animate: boolean }) {
  return (
    <section className="recap recap-fill" data-testid="sandbox-recap" aria-labelledby="sandbox-recap-title">
      <p className="eyebrow">The sandbox you built</p>
      <h1 className="h1 recap-title" id="sandbox-recap-title">
        Let the agent work. <em>Keep the boundaries.</em>
      </h1>
      <div className="card recap-diagram">
        <Blueprint animate={animate} />
      </div>
      <button className="btn primary big block" onClick={onDone} data-testid="sandbox-recap-continue">
        Continue →
      </button>
    </section>
  );
}

/** The kits pitch, as a picture: customize what's inside the box, keep the boundary. Stays until the player continues. */
export function KitsRecap({ onDone, animate }: { onDone: () => void; animate: boolean }) {
  return (
    <section className="recap recap-fill" data-testid="kits-recap" aria-labelledby="kits-recap-title">
      <p className="eyebrow">Make it your own</p>
      <h1 className="h1 recap-title" id="kits-recap-title">
        Customize the box. <em>Keep the boundary.</em>
      </h1>
      <div className="card recap-diagram">
        <KitDiagram animate={animate} />
      </div>
      <button className="btn primary big block" onClick={onDone} data-testid="kits-recap-continue">
        Continue →
      </button>
    </section>
  );
}

/** Finish screen: recap (right after a run), then the one reward QR CTA and an optional Learn link. */
export default function Reward() {
  const { quest, event, eventSlug, online, ready, refresh, track, lastResult, settings, pendingCount } = useQuest();
  const nav = useNavigate();
  const location = useLocation();
  const [summary] = useState(() => loadSummary(eventSlug));
  const [step, setStep] = useState<Step>(() =>
    (location.state as { justFinished?: boolean } | null)?.justFinished ? (loadSummary(eventSlug) ? 'results' : 'sandbox') : null,
  );
  const [claim, setClaim] = useState<Claim | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    document.title = `Reward · ${event.defenderTitle}`;
    void refresh();
    const iv = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While a QR is showing, poll so the screen flips to "redeemed" after staff scan it.
  useEffect(() => {
    if (!claim) return;
    const iv = window.setInterval(() => void refresh(), 4000);
    return () => window.clearInterval(iv);
  }, [claim, refresh]);

  // Each step opens at the top, not wherever the previous (shorter or taller) one was scrolled.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const endRecap = useCallback(() => {
    setStep(null);
    // Drop the one-shot flag so a reload goes straight to the reward.
    nav('/reward', { replace: true, state: null });
  }, [nav]);

  const show = useCallback(
    async (tier: RewardTier) => {
      setErr(null);
      try {
        const r = await Api.claim(tier);
        if (r.state === 'REDEEMED' || !r.signedQrPayload) {
          await refresh();
          setClaim(null);
          return;
        }
        const dataUrl = await QRCode.toDataURL(r.signedQrPayload, { errorCorrectionLevel: 'M', margin: 1, width: 560, color: { dark: '#0B0F19', light: '#FFFFFF' } });
        setClaim({ tier, dataUrl, payload: r.signedQrPayload, expiresAt: Date.parse(r.expiresAt) });
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Could not create a reward code.');
      }
    },
    [refresh],
  );

  if (!quest.completed && !lastResult) {
    if (!ready) return <p className="page eyebrow" role="status">Loading…</p>;
    return <Navigate to="/" replace />;
  }

  if (step === 'results' && summary) {
    return (
      <div className="app">
        <main className="page">
          <Results summary={summary} onDone={() => setStep('sandbox')} />
        </main>
      </div>
    );
  }
  if (step === 'sandbox') {
    return (
      <div className="app">
        <main className="page">
          <SandboxRecap onDone={() => setStep('kits')} animate={!settings.reducedMotion} />
        </main>
      </div>
    );
  }

  if (step === 'kits') {
    return (
      <div className="app">
        <main className="page">
          <KitsRecap onDone={endRecap} animate={!settings.reducedMotion} />
        </main>
      </div>
    );
  }

  const tiers = event.rewardPolicy.tiers.filter((t) => t.enabled);
  const secsLeft = claim ? Math.max(0, Math.round((claim.expiresAt - now) / 1000)) : 0;
  const redeemed = claim && quest.redeemedTiers.includes(claim.tier);
  const offlineRun = (lastResult?.offline && lastResult.result.outcome === 'won') || pendingCount > 0;
  const rejected = lastResult && !lastResult.offline && !lastResult.accepted && !quest.verified;
  const unverified = quest.completed && !quest.verified && !quest.dev;
  const playAgain = () => nav('/play', { state: { intent: 'start' } });

  return (
    <div className="app">
      <TopBar />
      <main className="page">
        <div>
          <p className="eyebrow">Run complete{quest.bestScore !== null ? ` · best score ${quest.bestScore}` : ''}</p>
          <h1 className="h2" style={{ marginTop: 6 }} data-testid="reward-title">
            {quest.verified || quest.dev ? `You're ${/^[aeiou]/i.test(event.defenderTitle) ? 'an' : 'a'} ${event.defenderTitle}.` : 'Nice run!'}
          </h1>
        </div>
        {!online && <div className="banner warn">You're offline. Reward codes need a connection so staff can verify them.</div>}
        {quest.dev && !quest.hideDevBanner && <div className="banner warn">DEV backend — codes from this server are for testing only.</div>}
        {offlineRun && (
          <div className="banner warn" role="status">
            You finished while offline. Your progress is saved on this phone, but rewards need a run the server can verify. Play again once
            you're online.
          </div>
        )}
        {rejected && !offlineRun && (
          <div className="banner warn" role="status">
            {REJECT_COPY[lastResult.rejectedReason ?? ''] ?? 'The server couldn’t verify this run.'} Play again online to earn the reward.
          </div>
        )}
        {unverified && !offlineRun && !rejected && (
          <div className="banner warn" role="status">This phone's run hasn't been verified yet. Play again online to earn the reward.</div>
        )}

        {claim && !redeemed && (
          <section className="card tier" aria-live="polite">
            <div className="tier-head">
              <span className="eyebrow">Reward code</span>
              <span className={`chip ${secsLeft < 60 ? 'warn' : 'ok'}`}>
                {secsLeft > 0 ? `Expires ${Math.floor(secsLeft / 60)}:${String(secsLeft % 60).padStart(2, '0')}` : 'Expired'}
              </span>
            </div>
            {secsLeft > 0 ? (
              <div className="qr-box">
                <img src={claim.dataUrl} alt={`QR reward code (${claim.tier})`} data-testid="claim-qr" data-payload={claim.payload} />
              </div>
            ) : (
              <button className="btn primary block" onClick={() => show(claim.tier)}>
                Generate a fresh code
              </button>
            )}
            <p className="muted" style={{ margin: 0, textAlign: 'center' }}>
              Show this at the booth. Staff scan it once. No personal info is inside the code.
            </p>
          </section>
        )}
        {claim && redeemed && (
          <div className="banner ok" role="status" data-testid="redeemed">
            ✓ Reward redeemed. Enjoy!
          </div>
        )}
        {err && <div className="banner danger" role="alert">{err}</div>}

        {tiers.map((t) => {
          const eligible = quest.rewardEligibility.includes(t.tier);
          const done = quest.redeemedTiers.includes(t.tier);
          if (claim?.tier === t.tier && !done) return null;
          return (
            <section key={t.tier} className="card tier">
              <div className="tier-head">
                <b style={{ fontSize: 18 }}>{t.label}</b>
                <span className={`chip ${eligible && !done ? 'ok' : ''}`}>{done ? 'Redeemed' : eligible ? 'Earned' : 'Not yet'}</span>
              </div>
              <span className="muted">{t.description}</span>
              {eligible && !done && (
                <button className="btn primary big block" data-testid="show-claim" onClick={() => show(t.tier)} disabled={!online}>
                  Show my reward QR
                </button>
              )}
            </section>
          );
        })}

        <LeaderboardRow eventSlug={eventSlug} refreshKey={quest.bestScore} />

        <a className="btn block" href={event.learnUrl} target="_blank" rel="noreferrer" onClick={() => track('guide_opened')} data-testid="learn">
          Learn: “What is a sandbox?” ↗
        </a>
        <div className="row-2">
          {summary && (
            <button className="btn ghost" onClick={() => setStep('results')}>
              See your results
            </button>
          )}
          <button className="btn ghost" onClick={playAgain} data-testid="play-again">
            Play again
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
